import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import objectArray from 'src/utils/objectArray';
import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';
import { getDefaultKanbanMode } from 'src/utils/kanbanPreferences';
import { getInvoiceAmountDue } from 'src/utils/tax';
import { socketClient } from '../index';

const BOARD_SEARCH_TIMEOUT_MS = 15000;
const PAID_SEARCH_TIMEOUT_MS = 15000;

let boardRequestSequence = 0;
let activeBoardRequestId = null;
let boardSearchTimeoutId = null;

let paidRequestSequence = 0;
let activePaidRequestId = null;
let paidSearchTimeoutId = null;
let dealerRequestSequence = 0;

const clearBoardSearchTimeout = () => {
  if (boardSearchTimeoutId) {
    window.clearTimeout(boardSearchTimeoutId);
    boardSearchTimeoutId = null;
  }
};

const clearPaidSearchTimeout = () => {
  if (paidSearchTimeoutId) {
    window.clearTimeout(paidSearchTimeoutId);
    paidSearchTimeoutId = null;
  }
};

const completeBoardRequest = (requestId) => {
  if (activeBoardRequestId && requestId && requestId !== activeBoardRequestId) {
    return false;
  }

  clearBoardSearchTimeout();
  activeBoardRequestId = null;
  return true;
};

const completePaidRequest = (requestId) => {
  if (activePaidRequestId && requestId && requestId !== activePaidRequestId) {
    return false;
  }

  clearPaidSearchTimeout();
  activePaidRequestId = null;
  return true;
};

const formatCurrencyValue = (value) => Number(value || 0).toFixed(2);

const getOptionalIdToken = async () => {
  try {
    const currentUser = firebase.auth().currentUser;
    return currentUser ? await currentUser.getIdToken() : null;
  } catch (_error) {
    return null;
  }
};

const getDealershipAuthConfig = async (config = {}) => {
  const idToken = await getOptionalIdToken();
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
    }
  };
};

const transitionWorkOrderStatus = async (id, status, cash, payment, reopen) => {
  const response = await axios.patch(
    buildApiUrl(`/api/operations/invoices/${id}/status`),
    {
      status,
      ...(typeof cash !== 'undefined' ? { cash } : {}),
      ...(payment ? { payment } : {}),
      ...(reopen ? { reopen } : {})
    },
    await getDealershipAuthConfig()
  );
  return response.data;
};

const transitionFailure = (error) => ({
  code: error?.response?.data?.code || 'work_order_transition_failed',
  message: error?.response?.data?.error || error?.message || 'Unable to update this work order.',
  paymentSummary: error?.response?.data?.paymentSummary || null
});

const buildBoardTask = (job, listId, color, preTaxValue, totalAmountDueValue) => ({
  id: job.id.toString(),
  attachments: 0,
  progress: 25,
  sub_items: 0,
  comments: 0,
  image: job.vehicle.photo || null,
  vehicleData: job.vehicle,
  vehicle: `${job.vehicle.year} ${job.vehicle.make} ${job.vehicle.model}`,
  vin: job.vehicle.vin,
  plate: job.vehicle.plate,
  mileage: job.odometer,
  mileageIn: job.odometer,
  mileageOut: job.odometer_at_completion,
  due_date: job.createdAt,
  listId,
  color,
  memberIds: [],
  name: job.vehicle.customer.name,
  customer: job.vehicle.customer,
  technician: job.technician,
  serviceAdvisorId: job.service_advisor_id || job.serviceAdvisor?.id || null,
  serviceAdvisorName: job.service_advisor_name_snapshot || job.serviceAdvisor?.name || null,
  workOrderLabels: job.workOrderLabels || job.work_order_labels || job.labels || [],
  preTax: formatCurrencyValue(preTaxValue),
  totalAmountDue: formatCurrencyValue(totalAmountDueValue),
  invoiceItems: job.invoiceItems
});

const buildBoardPayload = (response = {}) => {
  const data = {
    requestId: response.requestId || null,
    lists: [
      {
        id: '1',
        name: 'Quote',
        color: '#80cafc',
        taskIds: []
      },
      {
        id: '2',
        name: 'Approved',
        color: '#002dee',
        taskIds: []
      },
      {
        id: '3',
        name: 'Unpaid',
        color: '#7ed69f',
        taskIds: []
      }
    ],
    tasks: [],
    members: []
  };

  const approvedJobs = response.jobs || [];
  const invoiceJobs = response.invoices || [];
  const quoteJobs = response.stats?.quotes || [];

  for (let i = 0; i < approvedJobs.length; i += 1) {
    const job = approvedJobs[i];
    data.lists[1].taskIds.push(job.id.toString());
    data.tasks.push(
      buildBoardTask(
        job,
        '2',
        '#002dee',
        job.final_amount_charged || job.total_amount_charged,
        getInvoiceAmountDue(job)
      )
    );
  }

  for (let i = 0; i < invoiceJobs.length; i += 1) {
    const job = invoiceJobs[i];
    data.lists[2].taskIds.push(job.id.toString());
    data.tasks.push(
      buildBoardTask(
        job,
        '3',
        '#7ed69f',
        job.final_amount_charged,
        getInvoiceAmountDue(job)
      )
    );
  }

  for (let i = 0; i < quoteJobs.length; i += 1) {
    const job = quoteJobs[i];
    data.lists[0].taskIds.push(job.id.toString());
    data.tasks.push(
      buildBoardTask(
        job,
        '1',
        '#80cafc',
        job.final_amount_charged || 0,
        getInvoiceAmountDue(job)
      )
    );
  }

  return {
    ...data,
    quotePagination: {
      total: Number(response.stats?.quoteTotal ?? quoteJobs.length),
      limit: Number(response.stats?.quoteLimit ?? quoteJobs.length),
      hasMore: Boolean(response.stats?.quoteHasMore) ||
        Number(response.stats?.quoteTotal || 0) > quoteJobs.length
    },
    approvedPagination: {
      total: Number(response.stats?.approvedTotal ?? approvedJobs.length),
      limit: Number(response.stats?.approvedLimit ?? approvedJobs.length),
      hasMore: Boolean(response.stats?.approvedHasMore) ||
        Number(response.stats?.approvedTotal || 0) > approvedJobs.length
    },
    unpaidPagination: {
      total: Number(response.stats?.invoiceTotal ?? invoiceJobs.length),
      limit: Number(response.stats?.invoiceLimit ?? invoiceJobs.length),
      hasMore: Boolean(response.stats?.invoiceHasMore) ||
        Number(response.stats?.invoiceTotal || 0) > invoiceJobs.length
    }
  };
};

const createDealerColumns = () => ([
  {
    id: 'dealer-leads',
    name: 'LEADS',
    color: '#7c3aed',
    items: []
  },
  {
    id: 'dealer-test-drives',
    name: 'TEST DRIVES',
    color: '#ea580c',
    items: []
  },
  {
    id: 'dealer-paid',
    name: 'PAID',
    color: '#059669',
    items: []
  }
]);

const normalizeListResponse = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  return [];
};

const buildDealerBoardPayload = ({ leads = [], testDrives = [], contracts = [] } = {}) => {
  const columns = createDealerColumns();

  columns[0].items = leads.map((record) => ({
    id: `lead-${record.id}`,
    kind: 'lead',
    createdAt: record.createdAt,
    record
  }));

  columns[1].items = testDrives.map((record) => ({
    id: `test-drive-${record.id}`,
    kind: 'testDrive',
    createdAt: record.createdAt,
    record
  }));

  columns[2].items = contracts.map((record) => ({
    id: `contract-${record.id}`,
    kind: 'contract',
    createdAt: record.createdAt,
    record
  }));

  return { columns };
};

const initialState = {
  boardMode: getDefaultKanbanMode(),
  activeView: 'workflow',
  isLoaded: false,
  isRefreshing: false,
  searchQuery: '',
  searchError: null,
  activeRequestId: null,
  tasksArray: [],
  listsArray: [],
  lists: {
    byId: {},
    allIds: []
  },
  tasks: {
    byId: {},
    allIds: []
  },
  members: {
    byId: {},
    allIds: []
  },
  quotes: {
    limit: 10,
    total: 0,
    hasMore: false
  },
  approved: {
    limit: 100,
    total: 0,
    hasMore: false
  },
  unpaid: {
    limit: 100,
    total: 0,
    hasMore: false
  },
  paid: {
    rows: [],
    total: 0,
    page: 0,
    rowsPerPage: 25,
    searchQuery: '',
    isLoaded: false,
    isRefreshing: false,
    error: null,
    activeRequestId: null
  },
  dealer: {
    columns: createDealerColumns(),
    isLoaded: false,
    isRefreshing: false,
    searchQuery: '',
    error: null,
    activeRequestId: null
  }
};

const queueBoardRefresh = (dispatch, getState) => {
  window.setTimeout(() => {
    dispatch(requestGetAllData());

    if (getState().projectsBoard.paid.isLoaded) {
      dispatch(requestGetPaidData());
    }
  }, 200);
};

export const requestChangeStatusButtonClick = createAsyncThunk(
  'requestChangeStatusButtonClick',
  async function ({ id, destinationId, technician, mileageIn, mileageOut, paymentMethod, paymentReference, idempotencyKey, confirmReopen, reopenReason }, { getState, dispatch, rejectWithValue }) {
    const state = getState().projectsBoard;
    const task = state.tasks.byId[id];
    const taskTechnician = technician ?? task?.technician;
    const taskMileageIn = mileageIn ?? task?.mileageIn ?? task?.mileage;
    const taskMileageOut = mileageOut ?? task?.mileageOut ?? taskMileageIn;

    switch (destinationId) {
      case '1':
        try {
          const result = await transitionWorkOrderStatus(id, 'Quote');
          queueBoardRefresh(dispatch, getState);
          return result;
        } catch (error) {
          return rejectWithValue(transitionFailure(error));
        }
      case '2':
        try {
          const result = await transitionWorkOrderStatus(
            id,
            'Approved',
            undefined,
            undefined,
            confirmReopen ? { confirmed: true, reason: reopenReason } : undefined
          );
          queueBoardRefresh(dispatch, getState);
          return result;
        } catch (error) {
          return rejectWithValue(transitionFailure(error));
        }
      case '3':
        {
          try {
            const response = await axios.post(
              buildApiUrl(`/api/operations/invoices/${id}/finalize`),
              {
                technician: taskTechnician,
                mileageIn: taskMileageIn,
                mileageOut: taskMileageOut,
                vin: task?.vin,
                plate: task?.plate
              },
              await getDealershipAuthConfig()
            );
            queueBoardRefresh(dispatch, getState);
            return response.data;
          } catch (error) {
            return rejectWithValue({
              code: error?.response?.data?.code || 'work_order_finalization_failed',
              missingFields: error?.response?.data?.missingFields || [],
              fieldErrors: error?.response?.data?.fieldErrors || {},
              message: error?.response?.data?.error || error?.message || 'Unable to finalize this work order.'
            });
          }
        }
      case '4':
        try {
          if (!String(paymentMethod || '').trim() || !String(idempotencyKey || '').trim()) {
            return rejectWithValue({
              code: 'payment_confirmation_required',
              message: 'Select a payment method before marking this invoice paid.'
            });
          }
          const result = await transitionWorkOrderStatus(id, 'Delivered', undefined, {
            method: String(paymentMethod).trim(),
            reference: String(paymentReference || '').trim() || undefined,
            idempotencyKey: String(idempotencyKey).trim()
          });
          queueBoardRefresh(dispatch, getState);
          return result;
        } catch (error) {
          return rejectWithValue(transitionFailure(error));
        }
      default:
        break;
    }

    return null;
  }
);

export const requestChangeStatus = createAsyncThunk(
  'requestChangeStatus',
  async function ({ id, destinationId, draggableId, destination, confirmReopen, reopenReason }, { getState, dispatch, rejectWithValue }) {
    switch (destinationId) {
      case '1':
        try {
          const result = await transitionWorkOrderStatus(id, 'Quote');
          dispatch(moveTask(draggableId, destination.index, destination.droppableId));
          queueBoardRefresh(dispatch, getState);
          return result;
        } catch (error) {
          return rejectWithValue(transitionFailure(error));
        }
      case '2':
        try {
          const result = await transitionWorkOrderStatus(
            id,
            'Approved',
            undefined,
            undefined,
            confirmReopen ? { confirmed: true, reason: reopenReason } : undefined
          );
          dispatch(moveTask(draggableId, destination.index, destination.droppableId));
          queueBoardRefresh(dispatch, getState);
          return result;
        } catch (error) {
          return rejectWithValue(transitionFailure(error));
        }
      case '3':
        {
          return rejectWithValue({
            code: 'finalize_confirmation_required',
            message: 'Confirm technician and mileage before finalizing.'
          });
        }
      case '4':
        return rejectWithValue({
          code: 'payment_confirmation_required',
          message: 'Open the invoice and confirm its payment before marking it paid.'
        });
      default:
        break;
    }

    return null;
  }
);

export const deleteWorkOrder = createAsyncThunk(
  'deleteWorkOrder',
  async function (id, { dispatch, rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      await axios.delete(buildApiUrl(`/api/operations/invoices/${id}`), {
        headers: { Authorization: `Bearer ${idToken}` },
        data: { reason: 'Archived from the service workflow board' }
      });
      dispatch(requestGetAllData());
      return id;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error.message || 'Unable to archive the repair order.');
    }
  }
);

export const requestGetAllData = createAsyncThunk(
  'requestGetAllData',
  async function (payload = {}, { dispatch, getState }) {
    const state = getState().projectsBoard;
    const normalizedSearch = String(
      payload.search !== undefined ? payload.search : state.searchQuery || ''
    ).trim();
    const requestId = `${Date.now()}-${++boardRequestSequence}`;

    dispatch(slice.actions.setSearchQuery(normalizedSearch));
    activeBoardRequestId = requestId;
    clearBoardSearchTimeout();

    if (state.isLoaded) {
      dispatch(slice.actions.requestRefreshWorkOrders({ requestId }));
    } else {
      dispatch(slice.actions.requestGetWorkOrders({ requestId }));
    }

    boardSearchTimeoutId = window.setTimeout(() => {
      if (requestId !== activeBoardRequestId) {
        return;
      }

      dispatch(
        slice.actions.getWorkOrdersFailed({
          requestId,
          message: 'Search timed out. Please try a narrower search.'
        })
      );
    }, BOARD_SEARCH_TIMEOUT_MS);

    socketClient.emit('getAllData', {
      idToken: await firebase.auth().currentUser.getIdToken(),
      search: normalizedSearch || undefined,
      quoteLimit: Number(payload.quoteLimit || state.quotes.limit || 10),
      approvedLimit: Number(payload.approvedLimit || state.approved.limit || 100),
      invoiceLimit: Number(payload.invoiceLimit || state.unpaid.limit || 100),
      requestId
    });

    return requestId;
  }
);

export const requestGetPaidData = createAsyncThunk(
  'requestGetPaidData',
  async function (payload = {}, { dispatch, getState }) {
    const state = getState().projectsBoard;
    const normalizedSearch = String(
      payload.search !== undefined ? payload.search : state.paid.searchQuery || ''
    ).trim();
    const requestedPage = Number.isInteger(payload.page)
      ? payload.page
      : payload.search !== undefined
        ? 0
        : state.paid.page;
    const requestedLimit = Number.isInteger(payload.limit)
      ? payload.limit
      : state.paid.rowsPerPage;
    const requestId = `${Date.now()}-paid-${++paidRequestSequence}`;

    dispatch(slice.actions.setPaidSearchQuery(normalizedSearch));
    activePaidRequestId = requestId;
    clearPaidSearchTimeout();

    if (state.paid.isLoaded) {
      dispatch(
        slice.actions.requestRefreshPaidWorkOrders({
          requestId,
          page: requestedPage,
          rowsPerPage: requestedLimit
        })
      );
    } else {
      dispatch(
        slice.actions.requestGetPaidWorkOrders({
          requestId,
          page: requestedPage,
          rowsPerPage: requestedLimit
        })
      );
    }

    paidSearchTimeoutId = window.setTimeout(() => {
      if (requestId !== activePaidRequestId) {
        return;
      }

      dispatch(
        slice.actions.getPaidWorkOrdersFailed({
          requestId,
          message: 'Paid invoice search timed out. Please try a narrower search.'
        })
      );
    }, PAID_SEARCH_TIMEOUT_MS);

    socketClient.emit('getPaidWorkOrders', {
      idToken: await firebase.auth().currentUser.getIdToken(),
      search: normalizedSearch || undefined,
      page: requestedPage,
      limit: requestedLimit,
      requestId
    });

    return requestId;
  }
);

export const requestGetDealerData = createAsyncThunk(
  'requestGetDealerData',
  async function (payload = {}, { dispatch, getState, rejectWithValue }) {
    const state = getState().projectsBoard;
    const normalizedSearch = String(
      payload.search !== undefined ? payload.search : state.dealer.searchQuery || ''
    ).trim();
    const requestId = `${Date.now()}-dealer-${++dealerRequestSequence}`;

    dispatch(slice.actions.setDealerSearchQuery(normalizedSearch));

    if (state.dealer.isLoaded) {
      dispatch(slice.actions.requestRefreshDealerBoard({ requestId }));
    } else {
      dispatch(slice.actions.requestGetDealerBoard({ requestId }));
    }

    try {
      const params = {
        page: 1,
        pageSize: 50,
        ...(normalizedSearch ? { search: normalizedSearch } : {})
      };
      const dealershipAuthConfig = await getDealershipAuthConfig({ params });
      const [leadsResponse, testDrivesResponse, contractsResponse] = await Promise.all([
        axios.get(buildApiUrl('/sales/finance-applications/get'), dealershipAuthConfig),
        axios.get(buildApiUrl('/sales/test-drives/get'), dealershipAuthConfig),
        axios.get(buildApiUrl('/bill-of-sale/get'), dealershipAuthConfig)
      ]);

      dispatch(
        slice.actions.getDealerBoard({
          requestId,
          ...buildDealerBoardPayload({
            leads: normalizeListResponse(leadsResponse.data),
            testDrives: normalizeListResponse(testDrivesResponse.data),
            contracts: normalizeListResponse(contractsResponse.data)
          })
        })
      );

      return requestId;
    } catch (error) {
      dispatch(
        slice.actions.getDealerBoardFailed({
          requestId,
          message: error?.message || 'Unable to load dealer board.'
        })
      );
      return rejectWithValue(error?.message || 'Unable to load dealer board.');
    }
  }
);

export const getWorkOrders = createAsyncThunk('getAllData', async function (_, { dispatch }) {
  socketClient.off('getAllData');
  socketClient.off('getAllDataError');

  socketClient.on('getAllData', (response) => {
    if (!completeBoardRequest(response?.requestId || null)) {
      return;
    }

    dispatch({ type: 'projects_board/getWorkOrders', payload: buildBoardPayload(response) });
  });

  socketClient.on('getAllDataError', (response) => {
    if (!completeBoardRequest(response?.requestId || null)) {
      return;
    }

    dispatch(
      slice.actions.getWorkOrdersFailed({
        requestId: response?.requestId || null,
        message: response?.message || 'Unable to load work orders.'
      })
    );
  });
});

export const getPaidWorkOrders = createAsyncThunk(
  'getPaidWorkOrders',
  async function (_, { dispatch }) {
    socketClient.off('getPaidWorkOrders');
    socketClient.off('getPaidWorkOrdersError');

    socketClient.on('getPaidWorkOrders', (response) => {
      if (!completePaidRequest(response?.requestId || null)) {
        return;
      }

      dispatch(slice.actions.getPaidWorkOrders(response));
    });

    socketClient.on('getPaidWorkOrdersError', (response) => {
      if (!completePaidRequest(response?.requestId || null)) {
        return;
      }

      dispatch(
        slice.actions.getPaidWorkOrdersFailed({
          requestId: response?.requestId || null,
          message: response?.message || 'Unable to load paid invoices.'
        })
      );
    });
  }
);

const slice = createSlice({
  name: 'projects_board',
  initialState,
  reducers: {
    setBoardMode(state, action) {
      state.boardMode = action.payload || 'service';
    },
    setActiveView(state, action) {
      state.activeView = action.payload || 'workflow';
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload || '';
      state.searchError = null;
    },
    setPaidSearchQuery(state, action) {
      state.paid.searchQuery = action.payload || '';
      state.paid.error = null;
    },
    setDealerSearchQuery(state, action) {
      state.dealer.searchQuery = action.payload || '';
      state.dealer.error = null;
    },
    setPaidPage(state, action) {
      state.paid.page = Number.isInteger(action.payload) ? action.payload : 0;
    },
    setPaidRowsPerPage(state, action) {
      state.paid.rowsPerPage = Number.isInteger(action.payload) ? action.payload : 25;
      state.paid.page = 0;
    },
    setQuoteLimit(state, action) {
      state.quotes.limit = Math.max(10, Number(action.payload || 10));
    },
    setApprovedLimit(state, action) {
      state.approved.limit = Math.max(50, Number(action.payload || 100));
    },
    setUnpaidLimit(state, action) {
      state.unpaid.limit = Math.max(50, Number(action.payload || 100));
    },
    requestGetWorkOrders(state, action) {
      state.isLoaded = false;
      state.isRefreshing = true;
      state.activeRequestId = action.payload?.requestId || null;
      state.searchError = null;
    },
    requestRefreshWorkOrders(state, action) {
      state.isRefreshing = true;
      state.activeRequestId = action.payload?.requestId || null;
      state.searchError = null;
    },
    getWorkOrders(state, action) {
      if (
        action.payload?.requestId &&
        state.activeRequestId &&
        action.payload.requestId !== state.activeRequestId
      ) {
        return;
      }

      state.tasksArray = action.payload.tasks;
      state.listsArray = action.payload.lists;
      state.lists.byId = objectArray(action.payload.lists);
      state.lists.allIds = Object.keys(state.lists.byId);
      state.tasks.byId = objectArray(action.payload.tasks);
      state.tasks.allIds = Object.keys(state.tasks.byId);
      state.members.byId = objectArray(action.payload.members);
      state.members.allIds = Object.keys(state.members.byId);
      state.quotes.total = Number(action.payload.quotePagination?.total || 0);
      state.quotes.limit = Number(action.payload.quotePagination?.limit || state.quotes.limit || 10);
      state.quotes.hasMore = Boolean(action.payload.quotePagination?.hasMore);
      state.approved.total = Number(action.payload.approvedPagination?.total || 0);
      state.approved.limit = Number(action.payload.approvedPagination?.limit || state.approved.limit || 100);
      state.approved.hasMore = Boolean(action.payload.approvedPagination?.hasMore);
      state.unpaid.total = Number(action.payload.unpaidPagination?.total || 0);
      state.unpaid.limit = Number(action.payload.unpaidPagination?.limit || state.unpaid.limit || 100);
      state.unpaid.hasMore = Boolean(action.payload.unpaidPagination?.hasMore);
      state.isLoaded = true;
      state.isRefreshing = false;
      state.activeRequestId = null;
      state.searchError = null;
    },
    getWorkOrdersFailed(state, action) {
      if (
        action.payload?.requestId &&
        state.activeRequestId &&
        action.payload.requestId !== state.activeRequestId
      ) {
        return;
      }

      state.isRefreshing = false;
      state.activeRequestId = null;
      state.searchError = action.payload?.message || 'Unable to load work orders.';
    },
    requestGetPaidWorkOrders(state, action) {
      state.paid.isLoaded = false;
      state.paid.isRefreshing = true;
      state.paid.activeRequestId = action.payload?.requestId || null;
      state.paid.page = action.payload?.page ?? state.paid.page;
      state.paid.rowsPerPage = action.payload?.rowsPerPage ?? state.paid.rowsPerPage;
      state.paid.error = null;
    },
    requestRefreshPaidWorkOrders(state, action) {
      state.paid.isRefreshing = true;
      state.paid.activeRequestId = action.payload?.requestId || null;
      state.paid.page = action.payload?.page ?? state.paid.page;
      state.paid.rowsPerPage = action.payload?.rowsPerPage ?? state.paid.rowsPerPage;
      state.paid.error = null;
    },
    getPaidWorkOrders(state, action) {
      if (
        action.payload?.requestId &&
        state.paid.activeRequestId &&
        action.payload.requestId !== state.paid.activeRequestId
      ) {
        return;
      }

      state.paid.rows = action.payload?.rows || [];
      state.paid.total = Number(action.payload?.total || 0);
      state.paid.page = Number.isInteger(action.payload?.page)
        ? action.payload.page
        : state.paid.page;
      state.paid.rowsPerPage = Number.isInteger(action.payload?.limit)
        ? action.payload.limit
        : state.paid.rowsPerPage;
      state.paid.isLoaded = true;
      state.paid.isRefreshing = false;
      state.paid.activeRequestId = null;
      state.paid.error = null;
    },
    getPaidWorkOrdersFailed(state, action) {
      if (
        action.payload?.requestId &&
        state.paid.activeRequestId &&
        action.payload.requestId !== state.paid.activeRequestId
      ) {
        return;
      }

      state.paid.isRefreshing = false;
      state.paid.activeRequestId = null;
      state.paid.error = action.payload?.message || 'Unable to load paid invoices.';
    },
    requestGetDealerBoard(state, action) {
      state.dealer.columns = createDealerColumns();
      state.dealer.isLoaded = false;
      state.dealer.isRefreshing = true;
      state.dealer.activeRequestId = action.payload?.requestId || null;
      state.dealer.error = null;
    },
    requestRefreshDealerBoard(state, action) {
      state.dealer.isRefreshing = true;
      state.dealer.activeRequestId = action.payload?.requestId || null;
      state.dealer.error = null;
    },
    getDealerBoard(state, action) {
      if (
        action.payload?.requestId &&
        state.dealer.activeRequestId &&
        action.payload.requestId !== state.dealer.activeRequestId
      ) {
        return;
      }

      state.dealer.columns = action.payload?.columns || createDealerColumns();
      state.dealer.isLoaded = true;
      state.dealer.isRefreshing = false;
      state.dealer.activeRequestId = null;
      state.dealer.error = null;
    },
    getDealerBoardFailed(state, action) {
      if (
        action.payload?.requestId &&
        state.dealer.activeRequestId &&
        action.payload.requestId !== state.dealer.activeRequestId
      ) {
        return;
      }

      state.dealer.isRefreshing = false;
      state.dealer.activeRequestId = null;
      state.dealer.error = action.payload?.message || 'Unable to load dealer board.';
    },
    updateList(state, action) {
      const { list } = action.payload;
      state.lists.byId[list.id] = list;
    },
    moveTask(state, action) {
      const { taskId, position, listId } = action.payload;
      const sourceListId = state.tasks.byId[taskId].listId;
      const resolvedListId = listId || sourceListId;

      if (sourceListId) {
        const sourceList = state.lists.byId[sourceListId];
        state.lists.byId[sourceListId] = {
          ...sourceList,
          taskIds: sourceList.taskIds.filter((id) => id !== taskId)
        };
      }

      state.tasks.byId[taskId] = {
        ...state.tasks.byId[taskId],
        listId: resolvedListId
      };

      const destinationList = state.lists.byId[resolvedListId];
      state.lists.byId[resolvedListId] = {
        ...destinationList,
        taskIds: [
          ...destinationList.taskIds.slice(0, position),
          taskId,
          ...destinationList.taskIds.slice(position)
        ]
      };
    }
  }
});

export const reducer = slice.reducer;
export const {
  setBoardMode,
  setActiveView,
  setSearchQuery,
  setDealerSearchQuery,
  setPaidSearchQuery,
  setPaidPage,
  setPaidRowsPerPage,
  setQuoteLimit,
  setApprovedLimit,
  setUnpaidLimit
} = slice.actions;

export const updateList = (listId, update) => async (dispatch) => {
  const response = await axios.post('/api/projects_board/list/update', {
    listId,
    update
  });

  dispatch(slice.actions.updateList(response.data));
};

export const moveTask = (taskId, position, listId) => async (dispatch) => {
  dispatch(
    slice.actions.moveTask({
      taskId,
      position,
      listId
    })
  );
};

export default slice;
